import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import Cookies from 'js-cookie';
import { Currency, CountryCurrency, useRegionCurrency, type Country } from '@skin-seoul/service';
import { BridgeApi } from '@skin-seoul/utils';

interface CookieConsentStatus {
  hasFullConsent: boolean;
  hasAnalyticsConsent: boolean;
  hasAdvertisingConsent: boolean;
  isDeclined: boolean;
  requiresConsent: boolean;
}

// Cookie set by the Moove GDPR Cookie Compliance WordPress plugin.
// Same cookie the mobile app inspects in BridgeApi.handleGDPRBanner.
const MOOVE_GDPR_COOKIE = 'moove_gdpr_popup';

// Moove stores a JSON payload like:
//   { "strict": "1", "thirdparty": "0", "advanced": "1", "social": "0" }
// "strict" is always 1 (strictly necessary). The others are user choices.
interface MoovePayload {
  strict?: string | number | boolean;
  thirdparty?: string | number | boolean;
  advanced?: string | number | boolean;
  social?: string | number | boolean;
}

function isGranted(value: MoovePayload[keyof MoovePayload]): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return value === '1' || value.toLowerCase() === 'true';
}

function doesRequireGDPRConsent(country: Country): boolean {
  return CountryCurrency[country] === Currency.Europe;
}

function readConsentFromCookie(): MoovePayload | null {
  const raw = Cookies.get(MOOVE_GDPR_COOKIE);
  if (!raw) {
    return null;
  }
  try {
    // js-cookie already URL-decodes; the value should be a JSON string.
    return JSON.parse(raw) as MoovePayload;
  } catch {
    return null;
  }
}

const CookieConsentStatusContext = createContext<CookieConsentStatus>({
  hasFullConsent: false,
  hasAnalyticsConsent: false,
  hasAdvertisingConsent: false,
  isDeclined: false,
  requiresConsent: true,
});

interface Props {
  children: ReactNode;
}

export function ShortcodeCookieConsentProvider({ children }: Props) {
  const { country } = useRegionCurrency();
  const isWebView = BridgeApi.isWebview();
  const requiresConsent = !isWebView && doesRequireGDPRConsent(country);

  const [payload, setPayload] = useState<MoovePayload | null>(() => (requiresConsent ? readConsentFromCookie() : null));

  // The Moove banner writes the cookie after user interaction; there's no
  // cross-event from the plugin. Refresh on focus / visibility change.
  useEffect(() => {
    if (!requiresConsent) {
      return;
    }

    const refresh = () => setPayload(readConsentFromCookie());

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [requiresConsent]);

  const status = useMemo<CookieConsentStatus>(() => {
    if (!requiresConsent) {
      return {
        hasFullConsent: true,
        hasAnalyticsConsent: true,
        hasAdvertisingConsent: true,
        isDeclined: false,
        requiresConsent: false,
      };
    }

    // EU user, no cookie yet → Moove banner hasn't been resolved, treat as denied.
    if (!payload) {
      return {
        hasFullConsent: false,
        hasAnalyticsConsent: false,
        hasAdvertisingConsent: false,
        isDeclined: false,
        requiresConsent: true,
      };
    }

    const hasAnalyticsConsent = isGranted(payload.advanced);
    const hasAdvertisingConsent = isGranted(payload.thirdparty);
    const hasSocialConsent = isGranted(payload.social);
    const hasFullConsent = hasAnalyticsConsent && hasAdvertisingConsent && hasSocialConsent;
    const anyDenied = !hasAnalyticsConsent || !hasAdvertisingConsent || !hasSocialConsent;

    return {
      hasFullConsent,
      hasAnalyticsConsent,
      hasAdvertisingConsent,
      isDeclined: anyDenied && !hasFullConsent,
      requiresConsent: true,
    };
  }, [requiresConsent, payload]);

  return <CookieConsentStatusContext.Provider value={status}>{children}</CookieConsentStatusContext.Provider>;
}

export function useCookieConsentStatus(): CookieConsentStatus {
  return useContext(CookieConsentStatusContext);
}
